import { useTranslation } from 'react-i18next'
import { ElasticSlider } from '../ui/elastic-slider'

interface SliderFieldProps {
  label: string
  /** null means the value is not set — only possible when `clearAt` is given. */
  value: number | null
  onChange: (value: number | null) => void
  min: number
  max: number
  step?: number
  /**
   * The position that reads as "not set" and reports null. Load and RPE are
   * optional targets on a block, and a slider has no other way to say "none";
   * fields that must always hold a number simply leave this out.
   */
  clearAt?: number
  /** How the current value reads out, e.g. `(v) => \`${v}%\``. */
  format?: (value: number) => string
  className?: string
}

/**
 * A value set by dragging a filled bar, with its name and its reading inside
 * the track. Bounds and step mirror what the server accepts, so the control
 * cannot produce a value the API would reject.
 */
export function SliderField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  clearAt,
  format,
  className,
}: SliderFieldProps) {
  const { t } = useTranslation()
  const position = value ?? clearAt ?? min

  return (
    <ElasticSlider
      label={label}
      value={position}
      min={min}
      max={max}
      step={step}
      className={className}
      formatValue={(current) =>
        clearAt != null && current === clearAt
          ? t('common.notSet', '—')
          : (format?.(current) ?? String(current))
      }
      onValueChange={(next) => onChange(clearAt != null && next === clearAt ? null : next)}
    />
  )
}
